import React, { useMemo } from 'react';
import LoadingPage from '@/pages/LoadingPage';
import { ModalContextProvider } from './contexts/ModalContextProvider';
import useUser from './lib/hooks/useUser';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { routes as routes_loggedout } from './routes_loggedout';
import { routes as routes_loggedin } from './routes_loggedin';

const App = () => {

  const {isLogin} = useUser();

  // Build the router lazily from the current auth state. Previously we kept
  // two long-lived createBrowserRouter instances at module scope; each one
  // cached the URL that was current when the bundle first loaded and
  // maintained its own history subscription, so they didn't observe each
  // other. After login, App swapped to the logged-in router but that router
  // still believed it was on /login and rendered nothing meaningful, which
  // is why an explicit reload was the only way to see the dashboard.
  // Creating the router inside useMemo([isLogin]) ensures every auth
  // transition spawns a fresh router that reads window.location at birth —
  // we also nudge /login → / on the way in so the logged-in tree has a path
  // it can actually match.
  const router = useMemo(() => {
    if (isLogin === true) {
      if (window.location.pathname === '/login') {
        window.history.replaceState({}, '', `/${window.location.search}${window.location.hash}`);
      }
      return createBrowserRouter(routes_loggedin);
    }
    return createBrowserRouter(routes_loggedout);
  }, [isLogin]);

  // The `key` forces React to unmount the previous RouterProvider entirely
  // when isLogin flips, rather than handing the new router instance to the
  // same provider. RouterProvider in react-router-dom 7 assumes a stable
  // router prop — it wires up its history subscription at mount time, so
  // swapping the prop leaves stale listeners that ignore navigation calls.
  // Remounting on auth transition rebuilds those listeners cleanly.
  return (
    <ModalContextProvider>
      <React.Suspense fallback={<LoadingPage />}>
        <RouterProvider key={isLogin === true ? 'in' : 'out'} router={router} />
      </React.Suspense>
    </ModalContextProvider>
  );
};

export default App;
